import {
  Extension,
  ParentConfig,
  callOrReturn,
  getExtensionField,
} from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactRenderer } from '@tiptap/react';
import { find } from 'linkifyjs';
import {
  FindFn,
  createAfterPastePlugin,
} from '../../../private/plugins/AfterPastePlugin';
import { EmbedSuggestionMenu } from './EmbedSuggestionMenu';
import { AfterPasteContext, EmbedSuggestionItem } from './shared/types';
import { embedSuggestionState } from './state';

declare module '@tiptap/core' {
  interface NodeConfig<Options, Storage> {
    /**
     * Add embed suggestion option to the node
     */
    addEmbedSuggestionOption?:
      | null
      | ((this: {
          name: string;
          options: Options;
          storage: Storage;
          parent: ParentConfig<NodeConfig<Options>>['addEmbedSuggestionOption'];
        }) => EmbedSuggestionItem[]);
  }
}

export const embedSuggestionExtension = Extension.create({
  priority: Number.MIN_SAFE_INTEGER,
  name: 'embedSuggestion',
  extendNodeSchema(extension) {
    const context = {
      name: extension.name,
      options: extension.options,
      storage: extension.storage,
    };

    const items = callOrReturn(
      getExtensionField(extension, 'addEmbedSuggestionOption'),
      context,
    );
    if (!items) return {};

    embedSuggestionState.getState().addEmbed(items);

    return {};
  },
  addProseMirrorPlugins() {
    const { editor } = this;

    const findLink: FindFn<AfterPasteContext> = (textContent) => {
      const link = find(textContent).find(
        (item) => item.isLink && item.value === textContent,
      );

      if (link) return { link: link.href };

      return null;
    };

    return [
      createAfterPastePlugin({
        editor,
        pluginKey: new PluginKey('embedSuggestion'),
        find: findLink,
        render() {
          let component: ReactRenderer | null = null;

          const onClose = () => {
            component?.destroy();
          };

          return {
            onStart: (props) => {
              component = new ReactRenderer(EmbedSuggestionMenu, {
                editor,
                props: { ...props, onClose },
              });
            },
            onUpdate: (props) => {
              component?.updateProps(props);
            },
            onExit: () => {
              onClose();
            },
          };
        },
      }),
    ];
  },
});
